/**
 * Keeps the inactivity lock from firing while a web user is working.
 *
 * The drawer resets its lock timer from a PanResponder, i.e. on a press. On a
 * phone every interaction is a touch, so that covers everything. On a desktop
 * POS the cashier types, scans (the scanner is a keyboard), scrolls with the
 * wheel and moves the mouse - none of which is a press - and the screen locked
 * in the middle of a sale. Those events now count as activity too.
 *
 * Throttled: pointer moves and key repeats arrive dozens of times a second,
 * and one reset per second is plenty for a timeout measured in minutes.
 */

import {useEffect, useRef} from 'react';

const THROTTLE_MS = 1000;
const EVENTS = ['keydown', 'wheel', 'pointermove', 'pointerdown'] as const;

export function useActivityReset(onActivity: () => void, enabled: boolean): void {
  const onActivityRef = useRef(onActivity);
  onActivityRef.current = onActivity;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let last = 0;
    const handle = () => {
      const now = Date.now();
      if (now - last >= THROTTLE_MS) {
        last = now;
        onActivityRef.current();
      }
    };
    EVENTS.forEach(name =>
      document.addEventListener(name, handle, {capture: true, passive: true}),
    );
    return () => {
      EVENTS.forEach(name => document.removeEventListener(name, handle, {capture: true}));
    };
  }, [enabled]);
}
